import { useRef, useEffect, useCallback, useState } from 'react';
import { resolveAssets } from '../utils/storageApi';
import { fetchBackgrounds } from '../utils/backgroundsApi';
import { PRESET_BG_URLS } from '../constants/backgrounds';

/**
 * useAssetHydration — Phase 5b + Phase 1 (S3-Everything) + Session 35 (preset auto-assign)
 *
 * Manages URL hydration for cloud-stored assets (videos AND backgrounds).
 *
 * When slides contain videoWidgets with asset_id OR bgAssetId, this hook:
 *   1. Collects all asset_ids from slides (video + background)
 *   2. Batch resolves them via POST /api/media/resolve
 *   3. Silently updates videoWidget.src and backgroundUrl in slides (without marking dirty)
 *   4. Tracks resolve errors (not_found, deleted, unconfirmed)
 *   5. Auto-refreshes URLs before they expire (presigned = 1 hour)
 *
 * Session 35: Also auto-assigns bgAssetId to slides using default preset backgrounds
 * (bgIndex but no bgAssetId). Fetches preset list from API, maps bgIndex → preset asset_id.
 * This ensures exported HTML contains S3 presigned URLs instead of local /api/uploads/ paths.
 *
 * @param {Array} slides - Current slides array
 * @param {Function} setSlides - Raw React state setter (NOT handleSlideChange)
 * @param {Object} dirtyRef - React ref for dirty tracking (never set by hydration)
 * @returns {{ resolveErrors: Object, isHydrating: boolean }}
 */
export function useAssetHydration(slides, setSlides, dirtyRef) {
  // Cache: asset_id → { download_url, expires_at, reason }
  const cacheRef = useRef(new Map());
  // Refresh timer
  const refreshTimerRef = useRef(null);
  // Track which asset_ids are currently being resolved (debounce)
  const pendingRef = useRef(new Set());
  // Flag to prevent hydration from triggering itself
  const hydratingRef = useRef(false);
  // Preset backgrounds cache: filename → asset_id (fetched once)
  const presetsRef = useRef(null);
  // Whether preset assignment is in progress
  const presetAssignRef = useRef(false);

  const [resolveErrors, setResolveErrors] = useState({});
  const [isHydrating, setIsHydrating] = useState(false);

  /**
   * Session 35: Fetch preset backgrounds from API and build filename→asset_id map.
   * Cached in presetsRef — only fetched once per hook lifetime.
   */
  const getPresetMapping = useCallback(async () => {
    if (presetsRef.current) return presetsRef.current;
    try {
      const data = await fetchBackgrounds();
      const mapping = {};
      if (data?.presets) {
        for (const preset of data.presets) {
          if (preset.filename && preset.asset_id) {
            mapping[preset.filename] = preset.asset_id;
          }
        }
      }
      presetsRef.current = mapping;
      return mapping;
    } catch (err) {
      console.warn('[AssetHydration] Failed to fetch presets:', err.message);
      return {};
    }
  }, []);

  /**
   * Session 35: Auto-assign bgAssetId to slides using default preset backgrounds.
   * Matches bgIndex → PRESET_BG_URLS → filename → preset asset_id.
   * Only runs once per carousel load. Does NOT mark slides dirty.
   */
  const assignPresetAssetIds = useCallback(async (slideArray) => {
    if (presetAssignRef.current) return false;
    if (!Array.isArray(slideArray)) return false;

    // Find slides that need preset assignment:
    // ONLY slides with NO backgroundUrl (relying on preset fallback via bgIndex)
    // Slides with custom backgroundUrl (even legacy local paths) are left alone —
    // the EXTERNAL_BASE_URL fix handles those via the public backend URL.
    const needsAssignment = [];
    for (let i = 0; i < slideArray.length; i++) {
      const slide = slideArray[i];
      if (slide?.bgAssetId) continue; // already has asset_id
      if (slide?.backgroundUrl) continue; // has a custom/legacy background URL
      if (slide?.bgSolid) continue; // solid color bg
      needsAssignment.push(i);
    }

    if (needsAssignment.length === 0) return false;

    presetAssignRef.current = true;
    try {
      const presetMap = await getPresetMapping();
      if (Object.keys(presetMap).length === 0) return false;

      // Don't set hydratingRef here — we WANT the subsequent effect run
      // to pick up the new bgAssetId values and resolve them via S3
      setSlides((prev) => {
        let changed = false;
        const next = prev.map((slide, i) => {
          if (!needsAssignment.includes(i)) return slide;

          // Determine which preset this slide uses
          const bgIndex = typeof slide.bgIndex === 'number' ? slide.bgIndex : i;
          const presetUrl = PRESET_BG_URLS[bgIndex % PRESET_BG_URLS.length];
          const filename = presetUrl?.split('/').pop(); // e.g., "forest-01.jpg"
          const assetId = presetMap[filename];

          if (assetId) {
            changed = true;
            console.log(`[AssetHydration] Assigned preset bgAssetId: slide ${i} → ${assetId} (${filename})`);
            return { ...slide, bgAssetId: assetId };
          }
          return slide;
        });
        return changed ? next : prev;
      });
      return true;
    } catch (err) {
      console.warn('[AssetHydration] Preset assignment failed:', err.message);
      return false;
    } finally {
      presetAssignRef.current = false;
    }
  }, [getPresetMapping, setSlides]);

  /**
   * Extract all unique asset_ids from slides that need resolution.
   * Includes both video asset_ids and background bgAssetIds.
   */
  const extractAssetIds = useCallback((slideArray) => {
    const ids = new Set();
    if (!Array.isArray(slideArray)) return ids;
    for (const slide of slideArray) {
      // Video widget asset_ids (ast_ or UUID format)
      const videoAssetId = slide?.videoWidget?.asset_id;
      if (videoAssetId && typeof videoAssetId === 'string' && videoAssetId.length > 8) {
        ids.add(videoAssetId);
      }
      // Image widget asset_ids (Session 61: S3 migration)
      const imageAssetId = slide?.imageWidget?.asset_id;
      if (imageAssetId && typeof imageAssetId === 'string' && imageAssetId.length > 8) {
        ids.add(imageAssetId);
      }
      // Background asset_ids (Phase 1: S3-Everything)
      // Accept any non-empty string asset_id (not just ast_ prefix)
      // Migration-discovered assets may have UUID format without ast_ prefix
      const bgAssetId = slide?.bgAssetId;
      if (bgAssetId && typeof bgAssetId === 'string' && bgAssetId.length > 8) {
        ids.add(bgAssetId);
      }
    }
    return ids;
  }, []);

  /**
   * Check if a cached entry is still valid (not expired, has URL).
   */
  const isCacheValid = useCallback((entry) => {
    if (!entry || !entry.download_url) return false;
    // Consider expired if less than 5 minutes remaining
    const now = Date.now();
    const safetyMargin = 5 * 60 * 1000; // 5 minutes
    return entry.expires_at > now + safetyMargin;
  }, []);

  /**
   * Apply resolved URLs to slides without marking dirty.
   * Updates both videoWidget.src and backgroundUrl.
   */
  const applyHydration = useCallback((cache) => {
    hydratingRef.current = true;
    setSlides((prev) => {
      let changed = false;
      const next = prev.map((slide) => {
        let slideChanged = false;
        let updatedSlide = slide;

        // Hydrate video widget
        const videoAssetId = slide?.videoWidget?.asset_id;
        if (videoAssetId) {
          const entry = cache.get(videoAssetId);
          if (entry && entry.download_url && slide.videoWidget.src !== entry.download_url) {
            slideChanged = true;
            updatedSlide = {
              ...updatedSlide,
              videoWidget: { ...updatedSlide.videoWidget, src: entry.download_url },
            };
          }
        }

        // Hydrate image widget (Session 61: S3 migration)
        const imageAssetId = slide?.imageWidget?.asset_id;
        if (imageAssetId) {
          const entry = cache.get(imageAssetId);
          if (entry && entry.download_url && slide.imageWidget.src !== entry.download_url) {
            slideChanged = true;
            updatedSlide = {
              ...updatedSlide,
              imageWidget: { ...updatedSlide.imageWidget, src: entry.download_url },
            };
          }
        }

        // Hydrate background URL (Phase 1: S3-Everything)
        const bgAssetId = slide?.bgAssetId;
        if (bgAssetId) {
          const entry = cache.get(bgAssetId);
          if (entry && entry.download_url && slide.backgroundUrl !== entry.download_url) {
            slideChanged = true;
            updatedSlide = {
              ...updatedSlide,
              backgroundUrl: entry.download_url,
            };
          }
        }

        if (slideChanged) {
          changed = true;
          return updatedSlide;
        }
        return slide;
      });

      // Return same reference if nothing changed (prevents unnecessary re-renders)
      return changed ? next : prev;
    });
    // Small delay to ensure React processes the state update before clearing flag
    setTimeout(() => { hydratingRef.current = false; }, 50);
  }, [setSlides]);

  /**
   * Resolve a batch of asset_ids and update cache + slides.
   */
  const resolveAndHydrate = useCallback(async (assetIds) => {
    if (assetIds.size === 0) return;

    // Filter out already-pending resolutions
    const toResolve = [...assetIds].filter((id) => !pendingRef.current.has(id));
    if (toResolve.length === 0) return;

    // Mark as pending
    toResolve.forEach((id) => pendingRef.current.add(id));
    setIsHydrating(true);

    try {
      const result = await resolveAssets(toResolve);
      const now = Date.now();
      const newErrors = {};

      for (const item of result.items) {
        if (item.download_url) {
          // Success — cache with expiry
          const expiresIn = (item.expires_in || 3600) * 1000;
          cacheRef.current.set(item.asset_id, {
            download_url: item.download_url,
            expires_at: now + expiresIn,
            reason: null,
          });
        } else {
          // Error state — cache the reason
          cacheRef.current.set(item.asset_id, {
            download_url: null,
            expires_at: 0,
            reason: item.reason || 'unknown',
          });
          newErrors[item.asset_id] = item.reason || 'unknown';
        }
      }

      // Apply hydration
      applyHydration(cacheRef.current);

      // Update error state
      setResolveErrors((prev) => {
        // Clear errors for successfully resolved assets
        const updated = { ...prev };
        for (const item of result.items) {
          if (item.download_url) {
            delete updated[item.asset_id];
          } else {
            updated[item.asset_id] = item.reason || 'unknown';
          }
        }
        return updated;
      });

    } catch (err) {
      console.error('[AssetHydration] Resolve failed:', err.message);
      // Don't update errors for network failures — might be temporary
    } finally {
      toResolve.forEach((id) => pendingRef.current.delete(id));
      setIsHydrating(false);
    }
  }, [applyHydration]);

  /**
   * Main effect: watch slides for asset_ids that need resolution.
   * Session 35: Also triggers preset assignment for slides without bgAssetId.
   */
  useEffect(() => {
    // Skip if this update was triggered by hydration itself
    if (hydratingRef.current) return;

    // Session 35: Auto-assign preset bgAssetId for slides using default presets only
    // Only for slides with NO backgroundUrl and NO bgAssetId (truly using FOREST_BG_URLS fallback)
    const hasUnassigned = Array.isArray(slides) && slides.some(
      s => !s?.bgAssetId && !s?.backgroundUrl && !s?.bgSolid
    );
    if (hasUnassigned && !presetAssignRef.current) {
      assignPresetAssetIds(slides);
      // Don't return — let normal hydration run too for slides that already have bgAssetId
    }

    const assetIds = extractAssetIds(slides);
    if (assetIds.size === 0) return;

    // Find asset_ids that need resolution (not in cache or expired)
    const needsResolve = new Set();
    for (const id of assetIds) {
      const cached = cacheRef.current.get(id);
      if (!isCacheValid(cached)) {
        needsResolve.add(id);
      }
    }

    if (needsResolve.size > 0) {
      // Small debounce to batch rapid slide changes
      const timer = setTimeout(() => {
        resolveAndHydrate(needsResolve);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // All cached — apply hydration (e.g., on carousel switch with cached URLs)
      applyHydration(cacheRef.current);
    }
  }, [slides, extractAssetIds, isCacheValid, resolveAndHydrate, applyHydration, assignPresetAssetIds]);

  /**
   * Refresh timer: re-resolve URLs before they expire.
   * Checks every 5 minutes for soon-to-expire URLs.
   */
  useEffect(() => {
    const checkInterval = 5 * 60 * 1000; // 5 minutes

    const refreshExpiring = () => {
      const assetIds = extractAssetIds(slides);
      if (assetIds.size === 0) return;

      const needsRefresh = new Set();
      for (const id of assetIds) {
        const cached = cacheRef.current.get(id);
        if (cached && cached.download_url && !isCacheValid(cached)) {
          needsRefresh.add(id);
        }
      }

      if (needsRefresh.size > 0) {
        console.log(`[AssetHydration] Refreshing ${needsRefresh.size} expiring URL(s)`);
        resolveAndHydrate(needsRefresh);
      }
    };

    refreshTimerRef.current = setInterval(refreshExpiring, checkInterval);
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [slides, extractAssetIds, isCacheValid, resolveAndHydrate]);

  /**
   * Clear cache when slides change completely (carousel switch).
   * This is detected by a different set of asset_ids.
   */
  const prevAssetIdsRef = useRef(new Set());
  useEffect(() => {
    const currentIds = extractAssetIds(slides);
    const prevIds = prevAssetIdsRef.current;

    // If the set of asset_ids changed completely, clear stale cache entries
    const staleIds = [...prevIds].filter((id) => !currentIds.has(id));
    for (const id of staleIds) {
      cacheRef.current.delete(id);
    }

    prevAssetIdsRef.current = currentIds;
  }, [slides, extractAssetIds]);

  return { resolveErrors, isHydrating };
}
